import { useEffect, useRef } from "react";

// Debounced auto-save for settings panels: once `value` has been stable for
// `delayMs` (and differs from the saved snapshot), `save` fires. A payload
// that fails is remembered and not retried until the value changes again, so
// a rejected save can't loop error toasts. Gate with `enabled` while data is
// loading or the relevant editor is closed — a pending timer is cancelled the
// moment it flips false, which also protects per-warehouse settings from
// saving against the wrong warehouse after a tab switch.
export const useAutoSave = <T,>(
    value: T,
    opts: {
        dirty: boolean;
        saving: boolean;
        enabled?: boolean;
        delayMs?: number;
        save: (value: T) => Promise<boolean>; // resolve false on failure
    },
) => {
    const { dirty, saving, enabled = true, delayMs = 1000, save } = opts;
    const failed = useRef<string | null>(null);
    // Always call the latest save closure — handlers are recreated per render.
    const saveRef = useRef(save);
    saveRef.current = save;

    useEffect(() => {
        if (!enabled || !dirty || saving) return;
        const sig = JSON.stringify(value);
        if (failed.current === sig) return;
        const id = window.setTimeout(async () => {
            const ok = await saveRef.current(value);
            failed.current = ok ? null : sig;
        }, delayMs);
        return () => window.clearTimeout(id);
    }, [value, dirty, saving, enabled, delayMs]);
};

export default useAutoSave;
